/**
 * 3D viewer for reinforced concrete slabs (losas) — supports the 4 supported
 * types:
 *
 *   · aligerada_1d : ribbed one-way slab — ribs (T-beams) along x with hollow
 *                    blocks (ladrillos) between them; top concrete slab acts as
 *                    a flange. The bottom of each rib carries the positive
 *                    reinforcement; over continuous supports the top bars sit
 *                    inside the flange.
 *   · maciza_1d    : solid one-way slab — a single concrete plate. Two layers
 *                    of bars: a bottom layer along x (positive moment) and a
 *                    top layer at each continuous support (negative moment).
 *                    A transverse "temperature steel" mesh sits in the top
 *                    layer perpendicular to the main reinforcement.
 *   · maciza_2d    : solid two-way slab — same plate but with TWO orthogonal
 *                    bar layers at the bottom (one per direction). Marcus
 *                    distributes the live load between directions.
 *   · aligerada_2d : waffle slab — ribs in both directions intersecting in a
 *                    grid, with hollow blocks in each cell of the grid.
 *
 * Scene axes (matches the 2D plan view):
 *   x : long-direction span        (Lb for 2D, L_span for 1D)
 *   y : vertical                   (slab depth h_total)
 *   z : short-direction span       (La for 2D, lateral width for 1D)
 */

import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, GizmoHelper, GizmoViewport, Grid, Bounds } from '@react-three/drei';
import * as THREE from 'three';
import type { Continuity, SlabType } from './types';

interface Props {
  slabType: SlabType;
  /** Primary (or short, for 2D) span in cm. */
  L: number;
  /** Long-direction span in cm — only used for 2D types. */
  Lb: number;
  /** Total slab depth h (cm). */
  h: number;
  /** Top-slab (flange) thickness (cm) — only used for ribbed slabs. */
  t_slab: number;
  /** Rib width (cm) — only used for ribbed slabs. */
  bw: number;
  /** Rib spacing center-to-center (cm) — only used for ribbed slabs. */
  s: number;
  cover: number;
  /** Number of bottom (positive) bars per rib / per strip. */
  n_pos: number;
  /** Number of top (negative) bars per rib / per strip. */
  n_neg: number;
  /** Bar diameter (cm) — bottom bars. */
  d_bar_pos: number;
  /** Bar diameter (cm) — top bars. */
  d_bar_neg: number;
  /** Bottom bars in the LONG direction (only for 2D). */
  n_pos_long?: number;
  d_bar_pos_long?: number;
  /** Continuity in the primary (short) direction — drives which ends get top bars. */
  continuity: Continuity;
  /** Continuity in the long direction (2D only). */
  continuity_long?: Continuity;
}

const COLOR_CONCRETE = '#94A3B8';
const COLOR_CONCRETE_EDGE = '#94A3B8';
const COLOR_BRICK = '#A16207';        // hollow block (ladrillo) — terracotta
const COLOR_BAR_POS = '#F59E0B';      // bottom (positive moment) — amber
const COLOR_BAR_NEG = '#0891B2';      // top (negative moment) — teal
const COLOR_BAR_LONG = '#8B5CF6';     // long-direction bars (2D) — violet
const COLOR_BAR_TEMP = '#22C55E';     // temperature steel (top, transverse) — green

export function Slab3DView({
  slabType,
  L,
  Lb,
  h,
  t_slab,
  bw,
  s,
  cover,
  n_pos,
  n_neg,
  d_bar_pos,
  d_bar_neg,
  n_pos_long = 0,
  d_bar_pos_long = 0,
  continuity,
  continuity_long = 'one_end_continuous',
}: Props) {
  const isTwoWay = slabType === 'aligerada_2d' || slabType === 'maciza_2d';

  // For 1D types we render a lateral width corresponding to 3 ribs (so the
  // user sees 2 hollow blocks between them). For solid 1D we render a 1 m
  // strip representative of the design strip. For 2D we render the full panel
  // La × Lb.
  const width_cm = isTwoWay
    ? L                                  // La (short direction) in z
    : slabType === 'maciza_1d'
      ? 100                              // 1 m strip
      : 3 * s + bw;                      // 3 ribs + 2 cells

  const length_cm = isTwoWay ? Lb : L;

  // Uniform scale: keep the longest dimension within ~5 scene units, and the
  // shortest section dimensions within ~2.5 units. This keeps proportions
  // correct (a deep slab looks deep, a long panel looks long).
  const scale = Math.min(
    5 / Math.max(length_cm, 1),
    2.5 / Math.max(width_cm, 1),
    2.5 / Math.max(h, 1),
  );

  return (
    <Canvas
      camera={{ position: [6.5, 5, 6.5], fov: 38, near: 0.05, far: 100 }}
      style={{ width: '100%', height: '100%', background: 'var(--surface-canvas)' }}
      dpr={[1, 2]}
      gl={{ preserveDrawingBuffer: true }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 6, 5]} intensity={0.95} />
      <directionalLight position={[-4, -2, -3]} intensity={0.28} />

      <Grid
        args={[20, 20]}
        cellSize={0.25}
        cellThickness={0.4}
        cellColor="#475569"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#334155"
        fadeDistance={14}
        fadeStrength={1.2}
        position={[0, -h * scale * 0.5 - 0.01, 0]}
        infiniteGrid
      />

      <Bounds fit clip observe margin={1.35}>
        {slabType === 'aligerada_1d' && (
          <OneWayRibbed
            L={length_cm}
            h={h}
            t_slab={t_slab}
            bw={bw}
            s={s}
            cover={cover}
            n_pos={n_pos}
            n_neg={n_neg}
            d_bar_pos={d_bar_pos}
            d_bar_neg={d_bar_neg}
            continuity={continuity}
            scale={scale}
          />
        )}
        {slabType === 'maciza_1d' && (
          <OneWaySolid
            L={length_cm}
            h={h}
            cover={cover}
            n_pos={n_pos}
            n_neg={n_neg}
            d_bar_pos={d_bar_pos}
            d_bar_neg={d_bar_neg}
            continuity={continuity}
            scale={scale}
          />
        )}
        {slabType === 'maciza_2d' && (
          <TwoWaySolid
            La={L}
            Lb={Lb}
            h={h}
            cover={cover}
            n_pos_short={n_pos}
            n_pos_long={n_pos_long}
            n_neg={n_neg}
            d_bar_short={d_bar_pos}
            d_bar_long={d_bar_pos_long}
            d_bar_neg={d_bar_neg}
            continuity={continuity}
            continuity_long={continuity_long}
            scale={scale}
          />
        )}
        {slabType === 'aligerada_2d' && (
          <TwoWayRibbed
            La={L}
            Lb={Lb}
            h={h}
            t_slab={t_slab}
            bw={bw}
            s={s}
            cover={cover}
            n_pos={n_pos}
            n_neg={n_neg}
            d_bar_pos={d_bar_pos}
            d_bar_neg={d_bar_neg}
            n_pos_long={n_pos_long}
            d_bar_pos_long={d_bar_pos_long}
            continuity={continuity}
            continuity_long={continuity_long}
            scale={scale}
          />
        )}
      </Bounds>

      <OrbitControls makeDefault enableDamping dampingFactor={0.08} />
      <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
        <GizmoViewport axisColors={['#22C55E', '#F59E0B', '#3B82F6']} labelColor="white" />
      </GizmoHelper>
    </Canvas>
  );
}

/* =========================================================================
 *  ONE-WAY RIBBED SLAB  (aligerada_1d)
 *
 *  Geometry:
 *   - Top flange: full L × t_slab × W (W = 3·s + bw → 3 ribs visible)
 *   - 3 ribs spanning along x at z positions [-s, 0, +s], each L × (h-t) × bw
 *   - Hollow blocks (ladrillos) fill the 2 cells between the ribs
 *   - Bottom bars: along x at the bottom of every rib (positive moment)
 *   - Top bars: only over continuous ends, inside the flange
 * ========================================================================= */

function OneWayRibbed({
  L, h, t_slab, bw, s, cover,
  n_pos, n_neg, d_bar_pos, d_bar_neg, continuity, scale,
}: {
  L: number; h: number; t_slab: number; bw: number; s: number; cover: number;
  n_pos: number; n_neg: number; d_bar_pos: number; d_bar_neg: number;
  continuity: Continuity; scale: number;
}) {
  // Scene dimensions
  const Lx = L * scale;
  const Hy = h * scale;
  const t = t_slab * scale;
  const ribW = bw * scale;
  const ribH = (h - t_slab) * scale;
  const sp = s * scale;
  // 3 ribs centered at z = -s, 0, +s
  const ribZ = [-sp, 0, +sp];
  // Width of the flange: spans across the 3 ribs plus a small overhang on
  // each side (half a rib width).
  const flangeW = 2 * sp + ribW * 1.6;
  // Block (ladrillo) dimensions — fill between two adjacent ribs
  const blockW = (sp - ribW);       // gap between rib centers - one rib width
  const blockH = ribH;
  // Block centers between rib pairs (-s ↔ 0, 0 ↔ +s)
  const blockZ = [-sp / 2, +sp / 2];

  // Bar inset from rib bottom face
  const barInset = cover + d_bar_pos / 2;     // cm from bottom face
  const barInsetTop = cover + d_bar_neg / 2;  // cm from top face
  const barY_bot = (-Hy / 2) + barInset * scale;
  const barY_top = (Hy / 2) - barInsetTop * scale;
  const barR_pos = (d_bar_pos / 2) * scale;
  const barR_neg = (d_bar_neg / 2) * scale;

  // Continuous-end top bars: extend ~L/4 from each continuous support inside
  // the flange. Distributed across the flange width over each rib's tributary
  // width.
  const showLeftCont = continuity === 'one_end_continuous' || continuity === 'both_ends_continuous';
  const showRightCont = continuity === 'both_ends_continuous';
  const isCantilever = continuity === 'cantilever';
  const topBarLen = Lx * 0.30;   // ~L/4 + dev length

  return (
    <group>
      {/* Top flange (concrete slab on top) */}
      <mesh position={[0, Hy / 2 - t / 2, 0]}>
        <boxGeometry args={[Lx, t, flangeW]} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
      </mesh>
      <lineSegments position={[0, Hy / 2 - t / 2, 0]}>
        <edgesGeometry args={[new THREE.BoxGeometry(Lx, t, flangeW)]} />
        <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
      </lineSegments>

      {/* Ribs (3 visible) */}
      {ribZ.map((z, i) => (
        <group key={`rib-${i}`}>
          <mesh position={[0, -Hy / 2 + ribH / 2, z]}>
            <boxGeometry args={[Lx, ribH, ribW]} />
            <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
          </mesh>
          <lineSegments position={[0, -Hy / 2 + ribH / 2, z]}>
            <edgesGeometry args={[new THREE.BoxGeometry(Lx, ribH, ribW)]} />
            <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
          </lineSegments>

          {/* Bottom positive bars (n_pos within rib bw) */}
          {barZs(n_pos, bw, cover, d_bar_pos, scale, z).map((bz, k) => (
            <Cylinder
              key={`pos-${i}-${k}`}
              radius={barR_pos}
              length={Lx}
              position={[0, barY_bot, bz]}
              rotation={[0, 0, Math.PI / 2]}
              color={COLOR_BAR_POS}
            />
          ))}

          {/* Top negative bars (only if continuity exists) — short pieces over each cont end */}
          {showLeftCont && barZs(n_neg, bw, cover, d_bar_neg, scale, z).map((bz, k) => (
            <Cylinder
              key={`neg-L-${i}-${k}`}
              radius={barR_neg}
              length={topBarLen}
              position={[-Lx / 2 + topBarLen / 2, barY_top, bz]}
              rotation={[0, 0, Math.PI / 2]}
              color={COLOR_BAR_NEG}
            />
          ))}
          {showRightCont && barZs(n_neg, bw, cover, d_bar_neg, scale, z).map((bz, k) => (
            <Cylinder
              key={`neg-R-${i}-${k}`}
              radius={barR_neg}
              length={topBarLen}
              position={[Lx / 2 - topBarLen / 2, barY_top, bz]}
              rotation={[0, 0, Math.PI / 2]}
              color={COLOR_BAR_NEG}
            />
          ))}
          {isCantilever && barZs(n_neg, bw, cover, d_bar_neg, scale, z).map((bz, k) => (
            <Cylinder
              key={`neg-C-${i}-${k}`}
              radius={barR_neg}
              length={Lx}
              position={[0, barY_top, bz]}
              rotation={[0, 0, Math.PI / 2]}
              color={COLOR_BAR_NEG}
            />
          ))}
        </group>
      ))}

      {/* Hollow blocks (ladrillos) between the ribs — 2 visible cells */}
      {blockZ.map((bz, i) => (
        <mesh key={`block-${i}`} position={[0, -Hy / 2 + blockH / 2, bz]}>
          <boxGeometry args={[Lx, blockH, blockW]} />
          <meshStandardMaterial color={COLOR_BRICK} transparent opacity={0.75} roughness={1.0} metalness={0.05} />
        </mesh>
      ))}
      {/* Brick mortar lines: faint stripes on each block (horizontal joints) */}
      {blockZ.map((bz, i) => (
        [0.25, 0.5, 0.75].map((f, k) => (
          <mesh
            key={`mortar-${i}-${k}`}
            position={[0, -Hy / 2 + blockH * (1 - f), bz]}
          >
            <boxGeometry args={[Lx * 1.001, 0.004, blockW * 1.005]} />
            <meshBasicMaterial color="#451A03" />
          </mesh>
        ))
      ))}
    </group>
  );
}

/* =========================================================================
 *  ONE-WAY SOLID SLAB  (maciza_1d)
 *  Drawn as a 1 m wide strip representative of the design strip.
 * ========================================================================= */

function OneWaySolid({
  L, h, cover, n_pos, n_neg, d_bar_pos, d_bar_neg, continuity, scale,
}: {
  L: number; h: number; cover: number;
  n_pos: number; n_neg: number; d_bar_pos: number; d_bar_neg: number;
  continuity: Continuity; scale: number;
}) {
  const stripW = 100;
  const Lx = L * scale;
  const Hy = h * scale;
  const Wz = stripW * scale;

  const barInset = cover + d_bar_pos / 2;
  const barInsetTop = cover + d_bar_neg / 2;
  const barY_bot = (-Hy / 2) + barInset * scale;
  const barY_top = (Hy / 2) - barInsetTop * scale;
  const barR_pos = (d_bar_pos / 2) * scale;
  const barR_neg = (d_bar_neg / 2) * scale;

  const showLeftCont = continuity === 'one_end_continuous' || continuity === 'both_ends_continuous';
  const showRightCont = continuity === 'both_ends_continuous';
  const isCantilever = continuity === 'cantilever';
  const topBarLen = Lx * 0.30;

  // Distribute n_pos bars across the strip width
  const posZs = barZs(n_pos, stripW, cover, d_bar_pos, scale, 0);
  const negZs = barZs(n_neg, stripW, cover, d_bar_neg, scale, 0);

  // Temperature steel (perpendicular bars on top, every ~25 cm)
  const tempSpacing_cm = 25;
  const nTempBars = Math.max(3, Math.floor(L / tempSpacing_cm));
  const tempBarLen_scene = Wz * 0.98;

  return (
    <group>
      {/* Solid plate */}
      <mesh>
        <boxGeometry args={[Lx, Hy, Wz]} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(Lx, Hy, Wz)]} />
        <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
      </lineSegments>

      {/* Bottom positive bars (along x) */}
      {posZs.map((bz, k) => (
        <Cylinder
          key={`pos-${k}`}
          radius={barR_pos}
          length={Lx}
          position={[0, barY_bot, bz]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_POS}
        />
      ))}

      {/* Top negative bars over continuous supports */}
      {showLeftCont && negZs.map((bz, k) => (
        <Cylinder
          key={`neg-L-${k}`}
          radius={barR_neg}
          length={topBarLen}
          position={[-Lx / 2 + topBarLen / 2, barY_top, bz]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_NEG}
        />
      ))}
      {showRightCont && negZs.map((bz, k) => (
        <Cylinder
          key={`neg-R-${k}`}
          radius={barR_neg}
          length={topBarLen}
          position={[Lx / 2 - topBarLen / 2, barY_top, bz]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_NEG}
        />
      ))}
      {isCantilever && negZs.map((bz, k) => (
        <Cylinder
          key={`neg-C-${k}`}
          radius={barR_neg}
          length={Lx}
          position={[0, barY_top, bz]}
          rotation={[0, 0, Math.PI / 2]}
          color={COLOR_BAR_NEG}
        />
      ))}

      {/* Temperature steel — perpendicular bars on top, distributed along x */}
      {Array.from({ length: nTempBars }).map((_, i) => {
        const x = -Lx / 2 + ((i + 1) * Lx) / (nTempBars + 1);
        return (
          <Cylinder
            key={`temp-${i}`}
            radius={barR_neg * 0.7}
            length={tempBarLen_scene}
            position={[x, barY_top + 0.012, 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_TEMP}
          />
        );
      })}
    </group>
  );
}

/* =========================================================================
 *  TWO-WAY SOLID SLAB  (maciza_2d)
 *  Panel of La × Lb × h with two orthogonal bottom-bar layers.
 * ========================================================================= */

function TwoWaySolid({
  La, Lb, h, cover,
  n_pos_short, n_pos_long, n_neg,
  d_bar_short, d_bar_long, d_bar_neg,
  continuity, continuity_long, scale,
}: {
  La: number; Lb: number; h: number; cover: number;
  n_pos_short: number; n_pos_long: number; n_neg: number;
  d_bar_short: number; d_bar_long: number; d_bar_neg: number;
  continuity: Continuity; continuity_long: Continuity; scale: number;
}) {
  // x = Lb (long), z = La (short)
  const Lx = Lb * scale;
  const Wz = La * scale;
  const Hy = h * scale;

  const barInset = cover + Math.max(d_bar_short, d_bar_long) / 2;
  const barInsetTop = cover + d_bar_neg / 2;
  const barY_bot = (-Hy / 2) + barInset * scale;
  const barY_botUp = (-Hy / 2) + (barInset + Math.max(d_bar_short, d_bar_long)) * scale;
  const barY_top = (Hy / 2) - barInsetTop * scale;
  const barR_short = (d_bar_short / 2) * scale;
  const barR_long = (d_bar_long / 2) * scale;
  const barR_neg = (d_bar_neg / 2) * scale;

  // Bars in SHORT direction (along z, going from one short edge to the other —
  // therefore the bar lies parallel to z, which means rotation around x).
  // Distribute across the long direction Lb.
  const nShortPerPanel = Math.max(n_pos_short, 6);   // visual count
  const nLongPerPanel = Math.max(n_pos_long, 6);

  // Negative top bars: at each continuous edge a "band" of bars perpendicular
  // to the edge extending ~L/4 inward.
  const showLeftCont_x = continuity_long === 'one_end_continuous' || continuity_long === 'both_ends_continuous';
  const showRightCont_x = continuity_long === 'both_ends_continuous';
  const showLeftCont_z = continuity === 'one_end_continuous' || continuity === 'both_ends_continuous';
  const showRightCont_z = continuity === 'both_ends_continuous';

  const topBarPenetration_x = Lx * 0.25;   // band length in long direction
  const topBarPenetration_z = Wz * 0.25;

  return (
    <group>
      {/* Solid plate */}
      <mesh>
        <boxGeometry args={[Lx, Hy, Wz]} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.40} roughness={0.85} />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(Lx, Hy, Wz)]} />
        <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
      </lineSegments>

      {/* Bottom bars in SHORT direction (parallel to z, distributed along x). */}
      {Array.from({ length: nShortPerPanel }).map((_, i) => {
        const x = -Lx / 2 + ((i + 1) * Lx) / (nShortPerPanel + 1);
        return (
          <Cylinder
            key={`short-${i}`}
            radius={barR_short}
            length={Wz * 0.985}
            position={[x, barY_bot, 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_POS}
          />
        );
      })}
      {/* Bottom bars in LONG direction (parallel to x, distributed along z).
          One row up so they don't intersect with the short bars. */}
      {Array.from({ length: nLongPerPanel }).map((_, i) => {
        const z = -Wz / 2 + ((i + 1) * Wz) / (nLongPerPanel + 1);
        return (
          <Cylinder
            key={`long-${i}`}
            radius={barR_long}
            length={Lx * 0.985}
            position={[0, barY_botUp, z]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_LONG}
          />
        );
      })}

      {/* Top NEGATIVE bands — at continuous edges. We draw the bars
          perpendicular to each edge, extending ~L/4 into the panel. */}
      {showLeftCont_x && Array.from({ length: Math.max(n_neg, 6) }).map((_, i) => {
        const z = -Wz / 2 + ((i + 1) * Wz) / (Math.max(n_neg, 6) + 1);
        return (
          <Cylinder
            key={`top-Lx-${i}`}
            radius={barR_neg}
            length={topBarPenetration_x}
            position={[-Lx / 2 + topBarPenetration_x / 2, barY_top, z]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_NEG}
          />
        );
      })}
      {showRightCont_x && Array.from({ length: Math.max(n_neg, 6) }).map((_, i) => {
        const z = -Wz / 2 + ((i + 1) * Wz) / (Math.max(n_neg, 6) + 1);
        return (
          <Cylinder
            key={`top-Rx-${i}`}
            radius={barR_neg}
            length={topBarPenetration_x}
            position={[Lx / 2 - topBarPenetration_x / 2, barY_top, z]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_NEG}
          />
        );
      })}
      {showLeftCont_z && Array.from({ length: Math.max(n_neg, 6) }).map((_, i) => {
        const x = -Lx / 2 + ((i + 1) * Lx) / (Math.max(n_neg, 6) + 1);
        return (
          <Cylinder
            key={`top-Lz-${i}`}
            radius={barR_neg}
            length={topBarPenetration_z}
            position={[x, barY_top, -Wz / 2 + topBarPenetration_z / 2]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_NEG}
          />
        );
      })}
      {showRightCont_z && Array.from({ length: Math.max(n_neg, 6) }).map((_, i) => {
        const x = -Lx / 2 + ((i + 1) * Lx) / (Math.max(n_neg, 6) + 1);
        return (
          <Cylinder
            key={`top-Rz-${i}`}
            radius={barR_neg}
            length={topBarPenetration_z}
            position={[x, barY_top, Wz / 2 - topBarPenetration_z / 2]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_NEG}
          />
        );
      })}

      {/* Edge beams — thin gray ribbons around the perimeter suggesting
          supports on all 4 sides. */}
      <EdgeBeams Lx={Lx} Wz={Wz} Hy={Hy} />
    </group>
  );
}

function EdgeBeams({ Lx, Wz, Hy }: { Lx: number; Wz: number; Hy: number }) {
  // Decorative thin "beams" running along each of the 4 edges, just below the
  // bottom face of the slab, suggesting the slab is supported on 4 sides.
  const beamH = Hy * 0.45;
  const beamW = Math.min(Lx, Wz) * 0.04;
  const y = -Hy / 2 - beamH / 2;
  return (
    <group>
      <mesh position={[0, y, -Wz / 2 - beamW / 2]}>
        <boxGeometry args={[Lx + beamW, beamH, beamW]} />
        <meshStandardMaterial color="#475569" transparent opacity={0.55} roughness={0.9} />
      </mesh>
      <mesh position={[0, y, +Wz / 2 + beamW / 2]}>
        <boxGeometry args={[Lx + beamW, beamH, beamW]} />
        <meshStandardMaterial color="#475569" transparent opacity={0.55} roughness={0.9} />
      </mesh>
      <mesh position={[-Lx / 2 - beamW / 2, y, 0]}>
        <boxGeometry args={[beamW, beamH, Wz + beamW]} />
        <meshStandardMaterial color="#475569" transparent opacity={0.55} roughness={0.9} />
      </mesh>
      <mesh position={[+Lx / 2 + beamW / 2, y, 0]}>
        <boxGeometry args={[beamW, beamH, Wz + beamW]} />
        <meshStandardMaterial color="#475569" transparent opacity={0.55} roughness={0.9} />
      </mesh>
    </group>
  );
}

/* =========================================================================
 *  TWO-WAY RIBBED SLAB  (aligerada_2d)
 *  Waffle slab — ribs in both directions intersecting in a grid, with hollow
 *  blocks in each cell of the grid. We draw a small number of cells in each
 *  direction (4×4 maximum) regardless of panel size, just for visualization.
 * ========================================================================= */

function TwoWayRibbed({
  La, Lb, h, t_slab, bw, s, cover,
  d_bar_pos, d_bar_pos_long,
  scale,
}: {
  La: number; Lb: number; h: number; t_slab: number; bw: number; s: number; cover: number;
  n_pos: number; n_neg: number; n_pos_long: number;
  d_bar_pos: number; d_bar_neg: number; d_bar_pos_long: number;
  continuity: Continuity; continuity_long: Continuity; scale: number;
}) {
  const Lx = Lb * scale;
  const Wz = La * scale;
  const Hy = h * scale;
  const t = t_slab * scale;
  const ribW = bw * scale;
  const ribH = (h - t_slab) * scale;

  // Number of cells in each direction — cap to keep things visually clean
  const nCellsX = Math.min(5, Math.max(2, Math.round(Lb / s)));
  const nCellsZ = Math.min(5, Math.max(2, Math.round(La / s)));
  // Distribute ribs at uniform fractions of the panel
  const ribXs = Array.from({ length: nCellsX + 1 }, (_, i) =>
    -Lx / 2 + (i * Lx) / nCellsX,
  );
  const ribZs = Array.from({ length: nCellsZ + 1 }, (_, i) =>
    -Wz / 2 + (i * Wz) / nCellsZ,
  );

  const barInset = cover + d_bar_pos / 2;
  const barY_bot = (-Hy / 2) + barInset * scale;
  const barY_botUp = (-Hy / 2) + (barInset + Math.max(d_bar_pos, d_bar_pos_long)) * scale;
  const barR_pos = (d_bar_pos / 2) * scale;
  const barR_long = (d_bar_pos_long / 2) * scale;

  return (
    <group>
      {/* Top flange */}
      <mesh position={[0, Hy / 2 - t / 2, 0]}>
        <boxGeometry args={[Lx, t, Wz]} />
        <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
      </mesh>
      <lineSegments position={[0, Hy / 2 - t / 2, 0]}>
        <edgesGeometry args={[new THREE.BoxGeometry(Lx, t, Wz)]} />
        <lineBasicMaterial color={COLOR_CONCRETE_EDGE} />
      </lineSegments>

      {/* Ribs along x (the ribs that go in the LONG direction Lb): at each ribZ */}
      {ribZs.map((rz, i) => (
        <group key={`rx-${i}`}>
          <mesh position={[0, -Hy / 2 + ribH / 2, rz]}>
            <boxGeometry args={[Lx, ribH, ribW]} />
            <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
          </mesh>
          {/* Bottom long-direction bar (single representative) */}
          <Cylinder
            radius={barR_long}
            length={Lx * 0.985}
            position={[0, barY_botUp, rz]}
            rotation={[0, 0, Math.PI / 2]}
            color={COLOR_BAR_LONG}
          />
        </group>
      ))}

      {/* Ribs along z (the ribs that go in the SHORT direction La): at each ribX */}
      {ribXs.map((rx, i) => (
        <group key={`rz-${i}`}>
          <mesh position={[rx, -Hy / 2 + ribH / 2, 0]}>
            <boxGeometry args={[ribW, ribH, Wz]} />
            <meshStandardMaterial color={COLOR_CONCRETE} transparent opacity={0.42} roughness={0.85} />
          </mesh>
          {/* Bottom short-direction bar */}
          <Cylinder
            radius={barR_pos}
            length={Wz * 0.985}
            position={[rx, barY_bot, 0]}
            rotation={[Math.PI / 2, 0, 0]}
            color={COLOR_BAR_POS}
          />
        </group>
      ))}

      {/* Hollow blocks in each cell of the grid (ribZs[i]..ribZs[i+1] × ribXs[j]..ribXs[j+1]) */}
      {ribZs.slice(0, -1).map((zStart, i) => (
        ribXs.slice(0, -1).map((xStart, j) => {
          const zEnd = ribZs[i + 1];
          const xEnd = ribXs[j + 1];
          const cx = (xStart + xEnd) / 2;
          const cz = (zStart + zEnd) / 2;
          // Each block fills the cell minus the surrounding rib widths
          const bx = (xEnd - xStart) - ribW;
          const bz = (zEnd - zStart) - ribW;
          if (bx <= 0 || bz <= 0) return null;
          return (
            <mesh key={`block-${i}-${j}`} position={[cx, -Hy / 2 + ribH / 2, cz]}>
              <boxGeometry args={[bx, ribH, bz]} />
              <meshStandardMaterial color={COLOR_BRICK} transparent opacity={0.75} roughness={1.0} />
            </mesh>
          );
        })
      ))}
      <EdgeBeams Lx={Lx} Wz={Wz} Hy={Hy} />
    </group>
  );
}

/* =========================================================================
 *  HELPERS
 * ========================================================================= */

/**
 * Compute the z-coordinates (scene units) of `n` bars distributed across a
 * rib/strip of width `bw` (cm), centered at `centerZ` (scene units), with
 * `cover` (cm) on each side plus the bar radius. Mirrors the 2D drawing.
 */
function barZs(
  n: number,
  bw: number,
  cover: number,
  d_bar: number,
  scale: number,
  centerZ: number,
): number[] {
  if (n <= 0) return [];
  const off = cover + d_bar / 2;
  const usable = bw - 2 * off;    // cm
  if (n === 1) return [centerZ];
  return Array.from({ length: n }, (_, i) => {
    const cm = -bw / 2 + off + (i * usable) / (n - 1);
    return centerZ + cm * scale;
  });
}

function Cylinder({
  radius,
  length,
  position,
  rotation,
  color,
}: {
  radius: number;
  length: number;
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
}) {
  // We memo the geometry per (radius, length) — cheap and prevents allocation
  // churn when many bars share the same dimensions.
  const geom = useMemo(
    () => new THREE.CylinderGeometry(radius, radius, length, 14),
    [radius, length],
  );
  return (
    <mesh position={position} rotation={rotation} geometry={geom}>
      <meshStandardMaterial color={color} metalness={0.45} roughness={0.4} />
    </mesh>
  );
}
